<template>
  <BlocksHeroSection class="bg-cardinal-900">Players</BlocksHeroSection>
  <ContainerBasic>
    <TableSortable
      :loading="pending"
      :total="total"
      :per-page="perPage"
      v-model:page="page"
      :page-count="pageCount"
      v-model:sort="sort"
      v-model:year="year"
      v-model:filters-shown="filtersShown"
      @update:sort="refresh"
      :ui="{
        tr: {
          base: `even:bg-neutral-100 dark:bg-neutral-900 dark:even:bg-neutral-950`,
        },
      }"
      :default-columns="defaultColumns"
      :selected-columns="selectedColumns"
      :columns="columns"
      :rows="players"
      @reset-filters="resetRefs"
      @selected-columns-update="onSelectedColumnsChange"
    >
      <template #filters="{ dictionaryRows, toggleDictionary }">
        <FiltersSport v-model="sport" />
        <FormKit type="text" label="Name" v-model="searchTermName" />
        <FormKit type="text" label="Team Name" v-model="searchTermTeamName" />
        <FiltersPosition v-model="position" />
        <FiltersClassification v-model="classification" />
        <FiltersConference v-model="conference" />
        <FiltersDivision v-model="division" />
        <FiltersTransferred v-model="transferred" />
      </template>
      <template #player_name-data="{ row }">
        <TableCellLink left :path="`/players/${row.player_id}`">
          {{ row.player_name }}
        </TableCellLink>
      </template>
      <template #classification-data="{ row }">
        {{ row.classification || '--' }}
      </template>
      <template #transferred-data="{ row }">
        <span v-if="row.transferred == 'TRUE'">
          <i
            class="fas fa-check mx-auto text-green-600 dark:text-green-400"
          ></i>
        </span>
        <span v-else>
          <TableCellBase> &nbsp; </TableCellBase>
        </span>
      </template>
      <template #previous_team_name-data="{ row }">
        <span
          v-if="
            row.previous_team_name && row.previous_team_name !== row.team_name
          "
        >
          <TableCellLink left :path="`/teams/${row.previous_team_id}`">{{
            row.previous_team_name
          }}</TableCellLink>
        </span>
        <span v-else>
          <TableCellBase left> -- </TableCellBase>
        </span>
      </template>
      <template #team_name-data="{ row }">
        <TableCellLink
          class="flex items-center gap-1"
          left
          :path="`/teams/${row.team_id}`"
          ><TeamLogo :team="row" size="tiny" />{{
            row.team_name
          }}</TableCellLink
        >
      </template>
      <template #conference_abbreviation-data="{ row }">
        <TableCellBase left>{{ row.conference_abbreviation }}</TableCellBase>
      </template>
    </TableSortable>
  </ContainerBasic>
</template>

<script setup lang="ts">
useHead({
  title: 'NCAA Player Database - Explore Player Stats and Rankings',
  meta: [
    {
      name: 'description',
      content:
        'Browse our extensive database of NCAA baseball players. Analyze stats, rankings, and performance metrics to uncover top talents.',
    },
  ],
});

import { usePlayersStore } from '~/stores/players.store';
import type { Database } from '~/types';
import { getQueryPagination } from '~/types/database-query.d';
import { formatNumber } from '~/lib/helpers/format-number';
import { useSyncWithQuery } from '~/composables/useSyncWithQuery';
import type { ValidTableName } from '~/types/supabase-client';
const nuxtApp = useNuxtApp();
const supabase = useSupabaseClient<Database>();
const page = ref(1);
const team = ref();
const perPage = ref(50);
const filtersShown = ref(false);
const searchTermName = ref('');
const searchTermTeamName = ref('');
const position = ref('');
const classification = ref('');
const conference = ref<number | null>(null);
const sport = ref<Sport>('Baseball');
const division = ref('D-I');
const transferred = ref('');
const year = ref(2025);
const playersStore = usePlayersStore();
const { total, pageCount } = storeToRefs(playersStore);
const { setTotal, setPageCount } = playersStore;
const store = useProfileStore();
const { hasCurrentBaseballPortalPass, hasCurrentSoftballPortalPass } =
  storeToRefs(store);

useInitialSportSelection(
  sport,
  hasCurrentBaseballPortalPass,
  hasCurrentSoftballPortalPass
);

let query = ref('');
const { fillQueryParamValuesFromRefs, stopWatchers } = useSyncWithQuery<
  string | number | undefined | null
>([
  { key: 'page', ref: page, type: 'number' },
  { key: 'conference', ref: conference, type: 'number' },
  { key: 'searchTermName', ref: searchTermName },
  { key: 'searchTermTeamName', ref: searchTermTeamName },
  { key: 'position', ref: position },
  { key: 'classification', ref: classification },
  { key: 'division', ref: division },
  { key: 'transferred', ref: transferred },
  { key: 'sport', ref: sport },
]);

const sort = ref<{ column: string; direction: 'asc' | 'desc' }>({
  column: 'player_name',
  direction: 'asc',
});

const columnsDictionary = [
  {
    key: 'player_name',
    label: 'Name',
    sortable: true,
    class: '!text-left',
  },
  {
    key: 'team_name',
    label: 'Team',
    sortable: true,
    class: '!text-left',
  },
  {
    key: 'conference_abbreviation',
    label: 'Conference',
    sortable: true,
    class: '!text-left',
  },
  {
    key: 'classification',
    label: 'Classification',
    sortable: true,
  },
  {
    key: 'position',
    label: 'Position',
    sortable: true,
  },
  {
    key: 'transferred',
    label: 'Transferred',
    sortable: true,
  },
  {
    key: 'previous_team_name',
    label: 'Previous Team',
    sortable: true,
    class: '!text-left',
  },
];
const TABLE_NAMES: ValidTableName[] = ['players'];
const { parseData, localDictionary, dictionaryFilterRows } =
  useDataDictionary(TABLE_NAMES);
const defaultColumns = transformColumns(
  columnsDictionary,
  localDictionary.value
);
const selectedColumns = ref(defaultColumns.filter((c) => c.enabledByDefault));
const columns = computed((): Column[] => {
  return defaultColumns.filter((column) => {
    return selectedColumns.value.find((col) => col.key === column.key);
  });
});
const onSelectedColumnsChange = ($event: Column[]) => {
  selectedColumns.value = $event;
};

const getPlayers = async () => {
  try {
    let {
      data: players,
      error,
      count,
    } = await (() => {
      const pagination = getQueryPagination(page.value, perPage.value);
      let r = supabase
        .from('players_list')
        .select(`*`, { count: 'exact' })
        .range(pagination.startIndex, pagination.endIndex)
        .limit(perPage.value)
        .order(sort.value.column, { ascending: sort.value.direction == 'asc' });

      if (position.value) {
        r = r.eq('position', position.value);
      }
      if (classification.value) {
        r = r.eq('classification', classification.value);
      }
      if (searchTermName.value && searchTermName.value.length >= 3) {
        r = r.ilike('player_name', `%${searchTermName.value}%`);
      }
      if (searchTermTeamName.value && searchTermTeamName.value.length >= 3) {
        r = r.ilike('team_name', `%${searchTermTeamName.value}%`);
      }
      if (conference.value) {
        r = r.eq('conference_id', conference.value);
      }
      if (division.value) {
        r = r.eq('division', division.value);
      }
      if (transferred.value) {
        r = r.eq('transferred', transferred.value);
      }
      if (year.value) {
        r = r.eq('year', year.value);
      }
      // if (sport.value) {
      //   r = r.eq('sport', sport.value);
      // }
      r = r.eq('sport', sport.value);
      return r;
    })();

    if (error) {
      return { players: [], count: 0, error };
    }

    if (count) {
      console.log('count: ', count);
      setTotal(count);
      setPageCount(count / perPage.value);
    } else {
      setTotal(0);
      setPageCount(0);
      console.log('no count');
    }

    return players;
  } catch (error) {
    console.error(error);
    return [];
  }
};
const filtersToWatch = [
  searchTermName,
  searchTermTeamName,
  position,
  classification,
  team,
  conference,
  division,
  transferred,
  year,
  sport,
];
const {
  data: players,
  pending,
  refresh,
} = await useAsyncData('players', getPlayers, {
  watch: filtersToWatch,
});
watch([page], () => {
  console.log('page changed, refreshing data');
  refresh();
});
watch(filtersToWatch, () => {
  console.log('filter changed, resetting page');
  page.value = 1;
});
const resetRefs = () => {
  console.log('resetting refs');
  searchTermName.value = '';
  searchTermTeamName.value = '';
  position.value = '';
  classification.value = '';
  conference.value = null;
  division.value = 'D-I';
  transferred.value = '';
  page.value = 1;
  sort.value = {
    column: 'player_name',
    direction: 'asc',
  };
};
onBeforeRouteLeave(() => {
  stopWatchers();
});
onMounted(() => {
  fillQueryParamValuesFromRefs();
});
</script>
